import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ImpactPill from "@/components/Impact/ImpactPill";
import MoreButton from "@/components/MoreButton/MoreButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import Table, { Column, TableProps } from "@/components/Table/Table";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useTranslation from "@/hooks/useTranslation";
import {
  Route,
  useGetInboundTransportDetail,
  useGetInboundYearsPerRoute,
} from "@/hooks/useTransport";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import {
  BranchesOutlined,
  DeleteOutlined,
  ExportOutlined,
  EditOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Alert, Button, Tooltip } from "antd";
import { useState } from "react";
import { useParams } from "react-router-dom";
import DeleteInboundTransportAction from "../DeleteInboundTransportAction";
import ExportInbondTransportAction from "../ExportInboundTransportAction";
import RouteRow from "../RouteRow/RouteRow";
import InboundRouteWizard from "../RouteWizard/InboundRouteWizard";
import InboundOrderRouteWizard from "../RouteWizard/InboundOrderRouteWizard";
import { transportUploadFilters } from "../constants";
import InboundTransportButtonGroup from "./InboundTransportButtonGroup";

export default function InboundTransportDetail() {
  const translation = useTranslation();

  useTrackOnLoad("Inbound Transport Route Viewed");

  const queryParams = useParams();
  const departureId = Number(queryParams.departureId);
  const arrivalId = Number(queryParams.arrivalId);
  const category = queryParams.category || "";

  const { year } = useDashboardYear();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const transportDetailColumns: Column[] = [
    {
      key: "order_id",
      label: translation.transport.orderID,
      searchable: true,
      maxWidth: "200px",
      minWidth: "140px",
      renderer: (record) => (
        <CrossReference to={`/orders/purchase_orders?search_order_id=${record.order_id}`}>
          {record.order_id}
        </CrossReference>
      ),
    },
    {
      key: "departure_address",
      label: translation.transport.departureAddress,
      maxWidth: "2fr",
    },
    {
      key: "arrival_address",
      label: translation.transport.arrivalAddress,
      maxWidth: "2fr",
    },
    {
      key: "transport_mode",
      label: translation.transport.modeOfTransport,
    },
    {
      key: "owned",
      label: "Owned or leased",
      maxWidth: "150px",
      renderer: (record) => (record.owned ? "Yes" : "No"),
    },
    {
      key: "impact",
      label: "Impact",
      minWidth: "230px",
      maxWidth: "230px",
      renderer: (route) => {
        return (
          <ImpactPill
            impact={!route.route_error ? route.impact : undefined}
            type="borderless"
          />
        );
      },
    },
    {
      label: translation.transport.status,
      key: "status",
      maxWidth: "130px",
      minWidth: "130px",
      filterable: true,
      renderer: (route) => {
        return (
          <Tooltip
            title={
              route.status === "error"
                ? `${route.route_error} Try editing or uploading new data.`
                : route.status === "calculating"
                  ? "Root is calculating the impact of this leg in the background. All you have to do is wait. ⏳"
                  : undefined
            }
          >
            <>
              <StatusTag status={route.status} />
            </>
          </Tooltip>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      maxWidth: "80px",
      minWidth: "80px",
      noLink: true,
      renderer: (route) => {
        return (
          <ActionsPopover
            actions={
              <InboundOrderRouteWizard
                orderId={route.order_id}
                departureId={departureId}
                arrivalId={arrivalId}
                transportCategory={category}
                year={year}
              >
                <ActionsPopOverItem icon={<EditOutlined />}>Edit</ActionsPopOverItem>
              </InboundOrderRouteWizard>
            }
          >
            <MoreOutlined />
          </ActionsPopover>
        );
      },
      align: "end",
    },
  ];

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns: transportDetailColumns,
    });

  const { data, isLoading, isFetching, error } = useGetInboundTransportDetail({
    year,
    departureId: departureId,
    arrivalId: arrivalId,
    transportCategory: category,
    pagination,
    filter,
    search,
    sort,
  });

  const { data: years, isLoading: yearsLoading } = useGetInboundYearsPerRoute({
    departureId: departureId,
    arrivalId: arrivalId,
    transportCategory: category,
  });

  if (error) {
    return (
      <Alert
        message="Error"
        description="An error occurred while loading the inbound transport details. Please try again later."
        type="error"
        showIcon
      />
    );
  }

  const categoryText = category ? ` in ${category}` : "";

  const title = data
    ? data.arrival_name
      ? `Inbound transport from ${data.departure_name} to ${data.arrival_name}${categoryText}`
      : `Inbound transport from ${data.departure_name} ${translation.transport.withoutCategory}`
    : "Loading...";

  const tableProps: TableProps<Route> = {
    columns: transportDetailColumns,
    availableFilters: transportUploadFilters,
    rowKey: "order_id",
    onSearchFilterSortPaginate,
    loading: isLoading || isFetching,
    data: data?.routes,
    totalRows: data?.total_count,
    pagination,
    sort,
    filter,
    search,
    rowRenderer: RouteRow,
    hideRaster: true,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <PageHeader
        loading={isLoading}
        title={title}
        right={<InboundTransportButtonGroup />}
      />
      <div>
        <TableHeader
          left={
            <>
              <YearSelector
                optionData={years}
                isLoading={yearsLoading}
                onChange={() => setPage(1)}
              />
              <PageButtonGroup>
                <InboundRouteWizard
                  arrivalId={arrivalId}
                  departureId={departureId}
                  transportCategory={category}
                  year={year}
                >
                  <Button icon={<BranchesOutlined />}>Edit</Button>
                </InboundRouteWizard>
                <ExportInbondTransportAction
                  route={{
                    departure_facility_id: departureId,
                    arrival_facility_id: arrivalId,
                    transport_category: category,
                  }}
                  year={year}
                >
                  <Button icon={<ExportOutlined />}>Export</Button>
                </ExportInbondTransportAction>
                <MoreButton
                  onOpenChange={setIsMoreOpen}
                  open={isMoreOpen}
                  actions={
                    <>
                      <DeleteInboundTransportAction
                        route={{
                          departure_facility_id: departureId,
                          arrival_facility_id: arrivalId,
                          transport_category: category,
                        }}
                        year={year}
                        onSuccess={() => setIsMoreOpen(false)}
                      >
                        <ActionsPopOverItem
                          icon={<DeleteOutlined />}
                          danger
                        >
                          Delete
                        </ActionsPopOverItem>
                      </DeleteInboundTransportAction>
                    </>
                  }
                />
              </PageButtonGroup>
            </>
          }
          right={
            <StatusTags
              data={data}
              loading={isLoading}
              calculatingTooltip="Root is calculating the impact of some transport legs in this route in the background. All you have to do is wait. ⏳"
              errorTooltip="Root has not been able to calculate the impact of some legs in this route. Hover over the error in a leg to see what's going wrong. 🧐"
            />
          }
        />
        <Table {...tableProps} />
      </div>
    </div>
  );
}
